import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, updateDoc, doc, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { getAuth } from "firebase/auth";
import { Spin } from "antd";

export default function LibraryPage() {
  const navigate = useNavigate();
  const auth = getAuth();
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [genreFilter, setGenreFilter] = useState("Tous");
  const [selected, setSelected] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [fullName, setFullName] = useState("");
  const [contactDocId, setContactDocId] = useState(null);

  const toggleTheme = () => {
    setDark(d => { localStorage.setItem("ws-theme", !d ? "dark" : "light"); return !d; });
  };
  const t = dark ? theme.dark : theme.light;

  useEffect(() => {
    const init = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          const snap = await getDocs(query(collection(db, "contact"), where("FirebaseId", "==", user.uid)));
          if (!snap.empty) {
            setFullName(snap.docs[0].data().fullname || "");
            setContactDocId(snap.docs[0].id);
          }
        } catch {}
      }
      try {
        const snap = await getDocs(collection(db, "livres"));
        setBooks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    init();
  }, []);

  const genres = ["Tous", ...Array.from(new Set(books.map(b => b.genre).filter(Boolean)))];

  const filtered = books.filter(book => {
    const q = search.toLowerCase();
    const matchSearch = !search ||
      book.title?.toLowerCase().includes(q) ||
      book.authors?.toLowerCase().includes(q);
    const matchGenre = genreFilter === "Tous" || book.genre === genreFilter;
    return matchSearch && matchGenre;
  });

  const available = filtered.filter(b => !b.emprunteur);
  const borrowed  = filtered.filter(b => b.emprunteur);

  const handleBorrow = async () => {
    if (!selected || !fullName) return;
    setRequesting(true);
    try {
      const today = new Date().toLocaleDateString("fr-TN");

      // Update livres doc
      await updateDoc(doc(db, "livres", selected.id), {
        emprunteur: fullName,
        date: today,
      });

      // Update contact doc — save borrowed book name
      if (contactDocId) {
        await updateDoc(doc(db, "contact", contactDocId), {
          Livre: selected.title,
        });
      }

      // Update local state instantly
      setBooks(prev => prev.map(b => b.id === selected.id
        ? { ...b, emprunteur: fullName, date: today }
        : b
      ));
      setSuccess(selected);
      setSelected(null);
    } catch (err) { console.error(err); }
    setRequesting(false);
  };

  return (
    <div style={{ background: t.bg, minHeight: "100vh" }}>
      <div style={{ maxWidth: 480, margin: "0 auto", paddingBottom: 40 }}>

        {/* Topbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/logo.PNG" alt="WeStudy" style={{ height: 36, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.text1 }}>Bibliothèque</div>
              <div style={{ fontSize: 11, color: t.text2 }}>{books.length} livres · retour le jour même</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={toggleTheme} style={{ ...s.toggleTrack, background: t.bg3, border: `1px solid ${t.border}` }}>
              <div style={{ ...s.toggleKnob, background: t.blue, transform: dark ? "translateX(16px)" : "translateX(0)" }} />
            </button>
            <button onClick={() => navigate("/")} style={{ fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer", background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>← Retour</button>
          </div>
        </div>

        {/* Same day warning */}
        <div style={{ margin: "14px 18px 0", background: t.redBg, border: `1px solid ${t.redBorder}`, borderRadius: 14, padding: "10px 14px", display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
          <div style={{ fontSize: 12, color: t.red, lineHeight: 1.5, fontWeight: 500 }}>
            Tout livre emprunté doit être <strong>rendu le jour même avant 00h00</strong>. Récupération à l'accueil uniquement.
          </div>
        </div>

        {/* Search */}
        <div style={{ padding: "12px 18px 0" }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Titre ou auteur..."
            style={{ width: "100%", height: 42, borderRadius: 10, border: `1px solid ${t.border}`, background: t.bg3, color: t.text1, padding: "0 14px", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
        </div>

        {/* Genre filter */}
        {genres.length > 1 && (
          <div style={{ display: "flex", gap: 8, padding: "10px 18px 0", overflowX: "auto", scrollbarWidth: "none" }}>
            {genres.map(g => (
              <button key={g} onClick={() => setGenreFilter(g)} style={{
                padding: "6px 14px", borderRadius: 20, whiteSpace: "nowrap", flexShrink: 0,
                border: `1.5px solid ${genreFilter === g ? t.blue : t.border}`,
                background: genreFilter === g ? t.blueBg : t.bg3,
                color: genreFilter === g ? t.blueText : t.text2,
                fontSize: 12, fontWeight: genreFilter === g ? 600 : 400, cursor: "pointer",
              }}>{g}</button>
            ))}
          </div>
        )}

        {/* Success banner */}
        {success && (
          <div style={{ margin: "12px 18px 0", background: t.greenBg, border: `1px solid ${t.green}33`, borderRadius: 14, padding: "12px 14px", display: "flex", gap: 10, alignItems: "center" }}>
            <span style={{ fontSize: 20 }}>✅</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.green }}>Emprunt enregistré !</div>
              <div style={{ fontSize: 12, color: t.text2, marginTop: 2 }}>
                Récupère <strong>{success.title}</strong> à l'accueil. N'oublie pas de le rendre aujourd'hui.
              </div>
            </div>
            <button onClick={() => setSuccess(null)} style={{ background: "none", border: "none", fontSize: 16, color: t.text3, cursor: "pointer" }}>✕</button>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}><Spin /></div>
        ) : (
          <div style={{ padding: "14px 18px 0" }}>

            {/* Available */}
            {available.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: t.green, marginBottom: 10 }}>
                  📚 Disponibles ({available.length})
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                  {available.map(book => (
                    <BookCard key={book.id} book={book} t={t} available onClick={() => setSelected(book)} />
                  ))}
                </div>
              </>
            )}

            {/* Borrowed */}
            {borrowed.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: t.text3, marginBottom: 10 }}>
                  📖 Empruntés ({borrowed.length})
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {borrowed.map(book => (
                    <BookCard key={book.id} book={book} t={t} available={false} onClick={null} />
                  ))}
                </div>
              </>
            )}

            {filtered.length === 0 && (
              <div style={{ textAlign: "center", padding: "40px 0", color: t.text3, fontSize: 13 }}>
                Aucun livre trouvé.
              </div>
            )}

            {/* Reminder */}
            <div style={{ marginTop: 24, background: t.amberBg, border: `1px solid ${t.amberBorder}`, borderRadius: 14, padding: "12px 14px", display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>🏠</span>
              <div style={{ fontSize: 12, color: t.amberText, lineHeight: 1.6 }}>
                📅 Le livre doit être <strong>rendu le jour même</strong> avant 00h00. Tout retard sera noté sur votre compte.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Borrow Modal */}
      {selected && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "flex-end", justifyContent: "center", padding: "0 0 24px" }}
          onClick={e => { if (e.target === e.currentTarget) setSelected(null); }}>
          <div style={{ width: "100%", maxWidth: 440, borderRadius: "20px 20px 16px 16px", background: t.cardBg, border: `1px solid ${t.border}`, boxShadow: "0 -4px 32px rgba(0,0,0,0.2)", padding: "20px 18px", animation: "slideUp 0.3s ease" }}>

            {/* Book preview */}
            <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 16 }}>
              <div style={{ width: 64, height: 90, borderRadius: 8, overflow: "hidden", background: t.bg3, flexShrink: 0 }}>
                {selected.cover_medium ? (
                  <img src={selected.cover_medium} alt={selected.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>📚</div>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: t.text1, lineHeight: 1.3, marginBottom: 4 }}>{selected.title}</div>
                <div style={{ fontSize: 12, color: t.text2, marginBottom: 4 }}>✍️ {selected.authors}</div>
                {selected.publish_date && <div style={{ fontSize: 11, color: t.text3 }}>📅 {selected.publish_date}</div>}
                {selected.number_of_pages > 0 && <div style={{ fontSize: 11, color: t.text3 }}>📄 {selected.number_of_pages} pages</div>}
              </div>
            </div>

            <div style={{ height: 1, background: t.border, marginBottom: 14 }} />

            {/* Same day warning in modal */}
            <div style={{ background: t.redBg, border: `1px solid ${t.redBorder}`, borderRadius: 10, padding: "10px 12px", fontSize: 12, color: t.red, marginBottom: 14, fontWeight: 500 }}>
              ⚠️ Ce livre doit être rendu <strong>aujourd'hui avant 00h00</strong>. Récupération à l'accueil uniquement.
            </div>

            {fullName && (
              <div style={{ background: t.bg3, borderRadius: 10, padding: "10px 14px", fontSize: 12, color: t.text2, marginBottom: 16 }}>
                👤 Emprunt au nom de : <strong style={{ color: t.text1 }}>{fullName}</strong>
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setSelected(null)}
                style={{ flex: 1, padding: "12px", borderRadius: 12, border: `1px solid ${t.border}`, background: t.bg3, color: t.text2, fontSize: 13, cursor: "pointer" }}>
                Annuler
              </button>
              <button onClick={handleBorrow} disabled={requesting || !fullName}
                style={{ flex: 2, padding: "12px", borderRadius: 12, border: "none", background: requesting ? t.bg3 : t.blue, color: requesting ? t.text3 : "#fff", fontSize: 13, fontWeight: 600, cursor: requesting ? "not-allowed" : "pointer" }}>
                {requesting ? "Envoi..." : "📚 Emprunter ce livre"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes slideUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

function BookCard({ book, t, available, onClick }) {
  const [imgError, setImgError] = useState(false);
  return (
    <div onClick={available ? onClick : undefined} style={{
      background: t.cardBg, border: `1.5px solid ${t.border}`, borderRadius: 14,
      display: "flex", gap: 12, padding: 12, alignItems: "flex-start",
      cursor: available ? "pointer" : "default", opacity: available ? 1 : 0.6,
      transition: "border-color 0.15s",
    }}
      onMouseEnter={e => { if (available) e.currentTarget.style.borderColor = t.blue; }}
      onMouseLeave={e => { if (available) e.currentTarget.style.borderColor = t.border; }}
    >
      {/* Cover */}
      <div style={{ width: 52, height: 72, borderRadius: 6, overflow: "hidden", background: t.bg3, flexShrink: 0 }}>
        {book.cover_medium && !imgError ? (
          <img src={book.cover_medium} alt={book.title} onError={() => setImgError(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>📚</div>
        )}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: t.text1, lineHeight: 1.3, marginBottom: 3 }}>{book.title}</div>
        <div style={{ fontSize: 11, color: t.text2, marginBottom: 4 }}>✍️ {book.authors}</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {book.publish_date && <span style={{ fontSize: 10, color: t.text3 }}>📅 {book.publish_date}</span>}
          {book.number_of_pages > 0 && <span style={{ fontSize: 10, color: t.text3 }}>· {book.number_of_pages}p</span>}
          {book.genre && <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 20, background: t.blueBg, color: t.blueText }}>{book.genre}</span>}
        </div>
      </div>

      {/* Status */}
      <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
        <div style={{ fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 20, background: available ? t.green : t.red, color: "#fff" }}>
          {available ? "Disponible" : "Emprunté"}
        </div>
        {available && <div style={{ fontSize: 9, color: t.text3 }}>Appuyer →</div>}
        {!available && book.date && <div style={{ fontSize: 9, color: t.text3 }}>depuis {book.date}</div>}
      </div>
    </div>
  );
}

const theme = {
  light: { bg:"#f8fafc",bg3:"#f1f5f9",border:"#e2e8f0",text1:"#0f172a",text2:"#64748b",text3:"#94a3b8",blue:"#2563eb",blueBg:"#eff6ff",blueText:"#1d4ed8",green:"#16a34a",greenBg:"#f0fdf4",amber:"#d97706",amberBg:"#fffbeb",amberText:"#b45309",amberBorder:"#fde68a",red:"#dc2626",redBg:"#fef2f2",redBorder:"#fecaca",cardBg:"#ffffff" },
  dark:  { bg:"#0f172a",bg3:"#1a2740",border:"#273549",text1:"#f1f5f9",text2:"#94a3b8",text3:"#475569",blue:"#378ADD",blueBg:"#1e3a5f",blueText:"#85B7EB",green:"#4ade80",greenBg:"#052e16",amber:"#fbbf24",amberBg:"#2a1e07",amberText:"#fcd34d",amberBorder:"#78350f",red:"#f87171",redBg:"#2d1515",redBorder:"#7f1d1d",cardBg:"#1e293b" },
};

const s = {
  toggleTrack: { width:36,height:20,borderRadius:20,cursor:"pointer",position:"relative",flexShrink:0 },
  toggleKnob:  { width:14,height:14,borderRadius:"50%",position:"absolute",top:2,left:2,transition:"transform 0.2s" },
};